/**
 * Step 7.3 -- the built pack manifest, and what happens when its format moves.
 *
 * `pack.manifest.json` is the client's only asset index, which makes it the one
 * artefact that outlives the code that wrote it: a pack sits in `assets/` (or on a
 * CDN) while the client ships again and again. So the reader has to answer three
 * questions rather than one -- can I read this, can I *repair* this, or must I say
 * plainly that I cannot?
 *
 *   manifest older than this reader   ->  migrate it, in order, one version at a time
 *   manifest the same version         ->  use it
 *   manifest newer than this reader   ->  refuse, and say which side is behind
 *
 * A migration is a pure function from one shape to the next. Keeping them ordered and
 * separate means a v1 pack reaches v3 by running v1->v2 then v2->v3, so adding a
 * version never means revisiting the older ones.
 *
 * This type is shared rather than declared in the client because the pipeline writes
 * it, the client reads it and the budget report measures it -- three packages that
 * must agree on one shape. `pack.py`'s `PACK_MANIFEST_VERSION` is checked against the
 * constant here by `pipeline check`, so the two cannot drift silently.
 */
import type { AnimationLayer } from './animation.ts';
import type { StanceClass } from './definition.ts';

/**
 * Bumped whenever a built pack stops being readable as-is.
 *
 * v1 -- the first built format.
 * v2 -- `weapons[].stances` became explicit. v1 left it to the client to infer that a
 *       rigged weapon meant "archer", which put stance knowledge in the renderer and
 *       made a third stance a code change rather than a pack change.
 * v3 -- `combatMotion` dropped. Pairing a movement clip with a combat clip is what an
 *       action variation's `base` does, and the rules were a second way to say it that
 *       no client ever read.
 */
export const PACK_MANIFEST_VERSION = 3;

export type TintChannel = 'R' | 'G' | 'B';

export interface ManifestVariant {
  id: string;
  /** Mesh name inside the slot GLB -- a slot ships every enabled variant in one file. */
  mesh: string;
  scope: 'male' | 'female' | 'shared';
  index: number;
  triangles: number;
  label?: string | null;
  default: boolean;
}

export interface ManifestSlot {
  slot: string;
  url: string;
  bytes: number;
  meshes: number;
  variants: ManifestVariant[];
}

export interface ManifestClip {
  id: string;
  /**
   * Which half of the body the clip was authored to carry in a composition.
   *
   * Classification, not an instruction: the clip drives all 51 joints and plays whole
   * unless something composes it. `additive` is the exception -- those are offsets,
   * never poses. See `ANIMATION_LAYERS`.
   */
  layer: AnimationLayer;
  gender: string;
  durationMs: number;
  default: boolean;
  label?: string | null;
}

export interface ManifestBundle {
  id: string;
  url: string;
  /** True for the families every stance uses, so they are fetched once. */
  shared: boolean;
  stance: StanceClass | null;
  bytes: number;
  clips: ManifestClip[];
}

export interface ManifestWeapon {
  id: string;
  url: string;
  socket: 'propLeft' | 'propRight';
  triangles: number;
  bytes: number;
  rigged: boolean;
  /** Which stances may carry it. Explicit since v2. */
  stances: StanceClass[];
  /** Prop clip URLs, keyed by the body clip they play alongside. */
  propClips: Record<string, string>;
}

export interface PackManifest {
  formatVersion: number;
  packId: string;
  name: string;
  catalogueRevision: string;
  skeleton: { bones: string[] };
  slots: ManifestSlot[];
  bundles: ManifestBundle[];
  weapons: ManifestWeapon[];
  colourways: { id: string; url: string }[];
  tintRegions: { id: string; url: string; channel: TintChannel; palette: string[] }[];
  presets: unknown[];
}

/** Raised when a manifest cannot be read, with a message aimed at whoever has to fix it. */
export class PackManifestError extends Error {
  constructor(
    message: string,
    readonly source: string,
  ) {
    super(`${source}: ${message}`);
    this.name = 'PackManifestError';
  }
}

/**
 * One step up the version ladder, keyed by the version it upgrades *from*.
 *
 * Migrations mutate a shallow copy and never read anything outside the manifest, so
 * an old pack on a CDN can be brought forward without the art it was built from.
 */
const MIGRATIONS: Record<number, (manifest: Record<string, unknown>) => Record<string, unknown>> = {
  /**
   * v1 -> v2: make `weapons[].stances` explicit.
   *
   * v1 clients inferred it -- a rigged weapon was the archer's bow, an unrigged one in
   * the right hand was the swordsman's sword, and anything else (the shields) was
   * unreachable. The migration reproduces exactly that inference rather than guessing
   * something better, so a migrated v1 pack offers precisely what it offered before.
   */
  1: (manifest) => ({
    ...manifest,
    weapons: ((manifest.weapons ?? []) as Record<string, unknown>[]).map((weapon) => ({
      ...weapon,
      stances:
        weapon.rigged === true
          ? ['archer']
          : weapon.socket === 'propRight'
            ? ['swordsman']
            : [],
    })),
  }),
  /**
   * v2 -> v3: drop `combatMotion`.
   *
   * The rules never reached a client -- the field was written, carried and ignored --
   * so dropping them loses nothing a player could see. What they expressed, a combat
   * clip over a movement clip, is an action variation with a `base`, which is
   * authored in the editor and lives in the curation document.
   */
  2: (manifest) => {
    const { combatMotion: _dropped, ...rest } = manifest;
    return rest;
  },
};

/** The oldest format this reader can still bring forward. */
export const OLDEST_READABLE_PACK_MANIFEST = Math.min(
  PACK_MANIFEST_VERSION,
  ...Object.keys(MIGRATIONS).map(Number),
);

/**
 * Validates, migrates and returns a built pack manifest.
 *
 * `source` is used only in error messages -- normally the URL it was fetched from --
 * because "which pack" is the first thing anyone needs when this fails.
 */
export function readPackManifest(raw: unknown, source: string): PackManifest {
  if (typeof raw !== 'object' || raw === null) {
    throw new PackManifestError('not a JSON object', source);
  }
  const manifest = raw as Record<string, unknown>;
  const version = manifest.formatVersion;

  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new PackManifestError(
      `no usable formatVersion (found ${JSON.stringify(version)}) -- this does not look like a built pack manifest`,
      source,
    );
  }

  if (version > PACK_MANIFEST_VERSION) {
    throw new PackManifestError(
      `built in pack format v${version}, but this build reads up to v${PACK_MANIFEST_VERSION}. ` +
        'The client is behind the pipeline -- update the client, or rebuild the pack with the pipeline this client shipped with.',
      source,
    );
  }

  if (version < OLDEST_READABLE_PACK_MANIFEST) {
    throw new PackManifestError(
      `pack format v${version} is older than v${OLDEST_READABLE_PACK_MANIFEST}, the oldest this build can migrate. ` +
        'Re-run `npm run pipeline -- pack <id>` to rebuild it.',
      source,
    );
  }

  let current = manifest;
  for (let from = version; from < PACK_MANIFEST_VERSION; from += 1) {
    const migrate = MIGRATIONS[from];
    if (!migrate) {
      throw new PackManifestError(
        `no migration from pack format v${from} to v${from + 1}; rebuild the pack`,
        source,
      );
    }
    current = migrate(current);
    current.formatVersion = from + 1;
  }

  // Structural check after migrating, so a migration that dropped a section is caught
  // here rather than as an undefined halfway through assembling a character.
  for (const key of ['packId', 'slots', 'bundles', 'weapons', 'colourways', 'tintRegions']) {
    if (current[key] === undefined) {
      throw new PackManifestError(`migrated manifest is missing "${key}"`, source);
    }
  }

  return current as unknown as PackManifest;
}
